import React, { useState, useEffect, useContext } from 'react';
import { NEUTRAL_GREY_150 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/colors';
import { SliderContext } from 'components/Slider';
import Icon from 'components/Icon';

import styled from './styles';

export interface SliderLabelProps extends React.ComponentPropsWithRef<'div'> {
  /**
   * This is the value of the label. The type could be string, image URL or node.
   */
  label?: string | React.ReactNode;
}

/** Single label for Slider.
 * This component is for adding custom string, image URL or image node.
 * All additional props will be forwarded to the root element.
 * When it is used, it should be passed in either renderMaxLabel or renderMinLabel function for replacing labels in Slider
 */
const SliderLabel = (props: SliderLabelProps) => {
  const { label, ...otherProps } = props;
  const [sliderLabel, setSliderLabel] = useState(null);
  const { disabled } = useContext(SliderContext);

  const isImageUrl = () => (typeof label === 'string' && label.match(/\.(jpeg|jpg|gif|png|svg|)$/) != null);


  const isPureString = () => (typeof label === 'string' && label.match(/\.(jpeg|jpg|gif|png|svg|)$/) == null);

  const isNode = () => React.isValidElement(label);

  useEffect(() => {
    if (isImageUrl()) {
      if (typeof label === 'string') {
        setSliderLabel(<Icon src={label} color={disabled ? NEUTRAL_GREY_150 : undefined} />);
      } else {
        setSliderLabel(<Icon color={disabled ? NEUTRAL_GREY_150 : undefined}>{label}</Icon>);
      }
    } else if (isPureString()) {
      setSliderLabel(label);
    } else if (isNode()) {
      setSliderLabel(React.cloneElement(label as React.ReactElement<any>, { color: disabled ? NEUTRAL_GREY_150 : undefined }));
    }
  }, [label, disabled]);

  return (
    <styled.SliderLabel {...otherProps}>
      {sliderLabel}
    </styled.SliderLabel>
  );
};

SliderLabel.defaultProps = {
  label: '',
};


SliderLabel.displayName = 'SliderLabel';

export default SliderLabel;